import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@goaltree/ui/components/dropdown-menu'
import { cn } from '@goaltree/ui/lib/utils'
import { useForm } from '@tanstack/react-form'
import {
  ArrowRight,
  ArrowUp,
  Brain,
  Check,
  ChevronDown,
  Circle,
  Dices,
  Link2,
  Loader2,
} from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import z from 'zod'

import { ImagePickButton } from '@/components/image-capture'
import { MicButton } from '@/components/mic-button'
import { useDictation } from '@/hooks/use-dictation'
import { useOnlineStatus } from '@/hooks/use-online-status'
import { isMacPlatform } from '@/lib/capture'
import { categoryFromText } from '@/lib/habit-category'
import { posthog } from '@/lib/posthog'

const TYPE_SPEED = 55
const PAUSE_AFTER_TYPE = 1800
const FADE_DURATION = 500
const CATEGORIES = ['health', 'career', 'mind', 'finance', 'relationships', 'creative'] as const
const SUBMIT_KBD = isMacPlatform() ? '⌘⏎' : 'Ctrl⏎'
const MAX_INPUT_HEIGHT = 240

function useTypingPlaceholder(goals: string[], paused = false) {
  const [displayed, setDisplayed] = useState('')
  const [goalIndex, setGoalIndex] = useState(0)
  const [fading, setFading] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (paused) return
    const current = goals[goalIndex] ?? ''

    if (fading) {
      // Whole phrase fades out, then swap to the next and type it in.
      timeoutRef.current = setTimeout(() => {
        setDisplayed('')
        setGoalIndex((i) => (i + 1) % goals.length)
        setFading(false)
      }, FADE_DURATION)
    } else if (displayed.length < current.length) {
      timeoutRef.current = setTimeout(
        () => setDisplayed(current.slice(0, displayed.length + 1)),
        TYPE_SPEED,
      )
    } else if (current.length > 0) {
      timeoutRef.current = setTimeout(() => setFading(true), PAUSE_AFTER_TYPE)
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [displayed, fading, goalIndex, paused])

  return { text: displayed, fading }
}

interface GoalInputProps {
  // category: an explicit (lowercase) pick, or undefined for Auto.
  onSubmit: (goalText: string, category?: string) => void
  isLoading: boolean
  onSurpriseMe?: () => void
  onCoachMe?: () => void
  surpriseMeLoading?: boolean
  prefillValue?: string
  onValueChange?: (value: string) => void
  onShareGoal?: (goalText: string) => void
  // Life area for this goal. Unset means Auto.
  category?: string | null
  onCategoryChange?: (category: string | null) => void
  // Tasks the user already has, read from a photo; onPhotoLocked replaces the picker (Free).
  onPhoto?: (file: File) => void
  onPhotoLocked?: () => void
  photoReading?: boolean
  // A labelled primary button ("Build my plan") in place of the arrow.
  submitLabel?: string
  // Replaces the text even after typing (Try chips, ideas); bump n to refill the same text.
  fill?: { text: string; n: number }
}

const chipClass =
  'flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-border/60 bg-background/60 px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-border hover:text-foreground disabled:pointer-events-none disabled:opacity-50'

function CategoryDot({ category }: { category: string }) {
  return (
    <span className="size-1.5 shrink-0 rounded-full" style={{ background: `var(--${category})` }} />
  )
}

export default function GoalInput({
  onSubmit,
  isLoading,
  onSurpriseMe,
  onCoachMe,
  surpriseMeLoading,
  prefillValue,
  onValueChange,
  onShareGoal,
  category: categoryProp,
  onCategoryChange,
  onPhoto,
  onPhotoLocked,
  photoReading = false,
  submitLabel,
  fill,
}: GoalInputProps) {
  const { t } = useTranslation()
  const online = useOnlineStatus()
  const exampleGoals = t('planner.exampleGoals', { returnObjects: true }) as string[]
  const [ownCategory, setOwnCategory] = useState<string | null>(null)
  const category = categoryProp !== undefined ? categoryProp : ownCategory
  function setCategory(next: string | null) {
    setOwnCategory(next)
    onCategoryChange?.(next)
  }

  const goalSchema = z.object({
    goalText: z.string().min(3, t('planner.goalMinLength')),
  })
  const form = useForm({
    defaultValues: { goalText: '' },
    onSubmit: ({ value }) => {
      onSubmit(value.goalText, category ?? undefined)
    },
    validators: { onSubmit: goalSchema },
  })

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const userEditedRef = useRef(false)
  const [value, setValue] = useState('')
  const hasInput = value.length > 0
  const dictation = useDictation({
    getValue: () => (form.getFieldValue('goalText') as string) ?? '',
    onChange: (v) => {
      userEditedRef.current = true
      form.setFieldValue('goalText', v)
      onValueChange?.(v)
      setValue(v)
    },
  })
  const { text: animatedPlaceholder, fading: placeholderFading } = useTypingPlaceholder(
    exampleGoals,
    hasInput || category !== null,
  )
  const guessed = category ?? (hasInput ? categoryFromText(value) : undefined)
  const showChips = (onSurpriseMe || onCoachMe) && online

  useEffect(() => {
    if (prefillValue && !userEditedRef.current) {
      form.setFieldValue('goalText', prefillValue)
      setValue(prefillValue)
    }
  }, [prefillValue])

  useEffect(() => {
    if (!fill) return
    userEditedRef.current = true
    form.setFieldValue('goalText', fill.text)
    setValue(fill.text)
    onValueChange?.(fill.text)
    const el = textareaRef.current
    if (el) {
      el.focus({ preventScroll: true })
      el.setSelectionRange(fill.text.length, fill.text.length)
    }
  }, [fill])

  useLayoutEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, MAX_INPUT_HEIGHT)}px`
  }, [value])

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
      className="w-full space-y-3"
    >
      <form.Field name="goalText">
        {(field) => (
          <div className="space-y-2">
            <div
              className={cn(
                'beam-border relative flex flex-col gap-2.5 rounded-2xl border border-border/60 bg-card/40 px-4 pt-3.5 pb-2.5 backdrop-blur-sm transition-[border-color,box-shadow] focus-within:border-primary/50 focus-within:shadow-[0_0_0_4px_color-mix(in_srgb,var(--primary)_13%,transparent)]',
                isLoading && 'opacity-50',
              )}
            >
              <textarea
                ref={textareaRef}
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onFocus={(e) => {
                  // interactive-widget=resizes-content shrinks the viewport when
                  // the keyboard opens; wait for that, then reveal the input so
                  // its bottom isn't left under the keyboard.
                  const el = e.currentTarget
                  setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300)
                }}
                onChange={(e) => {
                  userEditedRef.current = true
                  field.handleChange(e.target.value)
                  onValueChange?.(e.target.value)
                  setValue(e.target.value)
                }}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                    e.preventDefault()
                    form.handleSubmit()
                  }
                }}
                placeholder={
                  category ? t(`planner.categoryExample.${category}`) : animatedPlaceholder || ' '
                }
                rows={2}
                autoFocus
                disabled={isLoading}
                className={cn(
                  'block w-full resize-none overflow-y-auto bg-transparent text-base leading-relaxed placeholder:text-muted-foreground/50 [&::placeholder]:transition-opacity [&::placeholder]:duration-500 focus-visible:outline-none disabled:cursor-not-allowed',
                  !category && placeholderFading
                    ? '[&::placeholder]:opacity-0'
                    : '[&::placeholder]:opacity-100',
                )}
              />
              <div
                className={cn(
                  'flex items-end gap-1.5 border-t border-border/60 pt-2.5',
                  submitLabel && 'max-sm:flex-wrap',
                )}
              >
                {/* Chips wrap on narrow screens; mic + submit stay pinned bottom-right. */}
                <div
                  className={cn(
                    'flex min-w-0 flex-1 flex-wrap items-center gap-1.5',
                    submitLabel && 'max-sm:basis-full',
                  )}
                >
                  {showChips && onSurpriseMe && (
                    <button
                      type="button"
                      onClick={onSurpriseMe}
                      disabled={surpriseMeLoading || isLoading}
                      className={chipClass}
                    >
                      {surpriseMeLoading ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Dices className="h-3 w-3" />
                      )}
                      {t('planner.suggest.surpriseMe')}
                    </button>
                  )}
                  {showChips && onCoachMe && (
                    <button
                      type="button"
                      onClick={onCoachMe}
                      disabled={isLoading}
                      className={chipClass}
                    >
                      <Brain className="h-3 w-3" />
                      {t('planner.suggest.coachMe')}
                    </button>
                  )}
                  <span className="flex-1" />
                  {onShareGoal && hasInput && !isLoading && (
                    <button
                      type="button"
                      onClick={() => {
                        posthog.capture('share_link_created', { type: 'anonymous' })
                        onShareGoal(field.state.value)
                      }}
                      aria-label={t('planner.suggest.shareGoalPrompt')}
                      title={t('planner.suggest.shareGoalPrompt')}
                      className="flex size-[30px] cursor-pointer items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      <Link2 className="size-4" />
                    </button>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      disabled={isLoading}
                      aria-label={t('planner.categoryLabel')}
                      className={cn(
                        'flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-border/60 bg-background/60 px-2.5 py-1 text-xs font-medium transition-colors hover:border-border disabled:pointer-events-none',
                        guessed ? 'text-foreground' : 'text-muted-foreground',
                      )}
                    >
                      {guessed ? (
                        <CategoryDot category={guessed} />
                      ) : (
                        <Circle className="size-3" strokeWidth={2.25} />
                      )}
                      <span className="text-muted-foreground">{t('planner.categoryLabel')}:</span>
                      {guessed ? t(`goals.cat.${guessed}`) : t('planner.categoryAuto')}
                      <ChevronDown className="size-3 opacity-60" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44 rounded-xl p-1">
                      {[null, ...CATEGORIES].map((key) => (
                        <DropdownMenuItem
                          key={key ?? 'auto'}
                          onClick={() => {
                            setCategory(key)
                            textareaRef.current?.focus()
                          }}
                          className="cursor-pointer gap-2 rounded-lg px-2.5 py-1.5 text-sm"
                        >
                          {key ? (
                            <CategoryDot category={key} />
                          ) : (
                            <Circle className="size-3 text-muted-foreground" strokeWidth={2.25} />
                          )}
                          <span className="flex-1">
                            {key ? t(`goals.cat.${key}`) : t('planner.categoryAuto')}
                          </span>
                          {category === key && <Check className="size-3.5 text-primary-text" />}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="ml-auto flex shrink-0 items-center gap-1.5">
                  {onPhoto &&
                    !isLoading &&
                    (photoReading ? (
                      <span
                        role="status"
                        aria-label={t('voice.readingImage')}
                        title={t('voice.readingImage')}
                        className={cn(
                          'flex items-center justify-center text-muted-foreground',
                          submitLabel ? 'size-10' : 'size-8',
                        )}
                      >
                        <Loader2 className="size-4 animate-spin" />
                      </span>
                    ) : (
                      <ImagePickButton
                        iconOnly
                        label={t('photoTasks.addPhoto')}
                        hint={t('photoTasks.goalHint')}
                        onImage={onPhoto}
                        onLocked={onPhotoLocked}
                        className={cn(
                          'justify-center rounded-full hover:bg-muted',
                          submitLabel ? 'size-10' : 'size-8',
                        )}
                      />
                    ))}
                  {dictation.supported && !isLoading && (
                    <MicButton
                      listening={dictation.listening}
                      onClick={dictation.toggle}
                      className={submitLabel ? 'size-10' : 'size-8'}
                    />
                  )}
                  <form.Subscribe selector={(state) => state.isSubmitting}>
                    {(isSubmitting) => {
                      const ready = !!value.trim() && online && !isLoading && !isSubmitting
                      return (
                        <button
                          type="submit"
                          disabled={!ready}
                          aria-label={submitLabel ? undefined : t('planner.createPlan')}
                          title={submitLabel ? undefined : t('planner.createPlan')}
                          className={cn(
                            'flex shrink-0 items-center justify-center rounded-full transition-all',
                            submitLabel ? 'h-10 gap-1.5 px-4 text-sm font-extrabold' : 'size-8',
                            ready
                              ? 'cursor-pointer bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95'
                              : submitLabel
                                ? 'cursor-not-allowed bg-primary/35 text-primary-foreground/80'
                                : 'cursor-not-allowed bg-muted text-muted-foreground/50',
                          )}
                        >
                          {submitLabel && <span className="whitespace-nowrap">{submitLabel}</span>}
                          {isLoading || isSubmitting ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : submitLabel ? (
                            <ArrowRight className="size-4" strokeWidth={2.5} />
                          ) : (
                            <ArrowUp className="size-4" strokeWidth={2.5} />
                          )}
                        </button>
                      )
                    }}
                  </form.Subscribe>
                </div>
              </div>
            </div>
            {field.state.meta.errors.map((error, index) => (
              <p key={`${field.name}-error-${index}`} className="text-sm text-destructive">
                {error?.message}
              </p>
            ))}
          </div>
        )}
      </form.Field>

      <p className="text-center text-xs text-muted-foreground">
        {!online ? (
          t('offline.goalNeedsConnection')
        ) : (
          <>
            {hasInput && (
              <span className="hidden sm:inline">
                <kbd className="rounded-[5px] border border-border/70 px-1.5 py-px font-mono text-[10.5px] font-semibold">
                  {SUBMIT_KBD}
                </kbd>{' '}
                {t('planner.toCreate')}
                {' · '}
              </span>
            )}
            {t('planner.planIn')}
          </>
        )}
      </p>
    </form>
  )
}
